import React, { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { Form, FormInput, FormButton, Button } from '../../../Common';
import { PasswordData, passwordSchema, passwordDefaultValues } from './PasswordForm.helper';
import './PasswordForm.css';

type PasswordFormProps = {
  title?: string;
  subtitle?: string;
  buttonText?: string;
  passwordPlaceholder?: string;
  confirmPasswordPlaceholder?: string;
  onSubmit?: (data: PasswordData) => Promise<void> | void;
  onBackToSignIn?: () => void;
  className?: string;
};

const PasswordForm: React.FC<PasswordFormProps> = ({
  title = 'Create Password',
  subtitle = 'Please create a password for your account.',
  buttonText = 'Create password',
  passwordPlaceholder = 'Password',
  confirmPasswordPlaceholder = 'Confirm password',
  onSubmit,
  onBackToSignIn,
  className = '',
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const handleSubmit = async (data: PasswordData) => {
    try {
      await onSubmit?.(data);
    } catch (error) {
      console.error('Password form failed:', error);
      throw error;
    }
  };

  return (
    <div className={`password-form-container ${className}`}>
      <div className="password-form-title-section">
        <h1 className="password-form-title">{title}</h1>
        <p className="password-form-subtitle">{subtitle}</p>
      </div>
      
      <div className="password-form-section">
        <Form
          onSubmit={handleSubmit}
          className="password-form-fields"
          defaultValues={passwordDefaultValues}
          resolver={yupResolver(passwordSchema)}
        >
          <FormInput
            name="password"
            placeholder={passwordPlaceholder}
            type={showPassword ? 'text' : 'password'}
            required
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      className="password-toggle-button"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />

          <FormInput
            name="confirmPassword"
            placeholder={confirmPasswordPlaceholder}
            type={showConfirmPassword ? 'text' : 'password'}
            required
            fullWidth
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                      className="password-toggle-button"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          
          <FormButton type="submit" primary size="full">
            {buttonText}
          </FormButton>
        </Form>

        {onBackToSignIn && (
          <Button
            type="button"
            onClick={onBackToSignIn}
            primary={false}
            bordered={false}
            size="full"
          >
            Back to Sign In
          </Button>
        )}
      </div>
    </div>
  );
};

export default PasswordForm;
